import { useEffect, useMemo, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, Empty } from 'antd';
import classNames from 'classnames';
import type { AppProps } from 'next/app';
import localFont from 'next/font/local';
import Head from 'next/head';
import { useRouter } from 'next/router';
import NextAdapterApp from 'next-query-params/app';
import { QueryParamProvider } from 'use-query-params';

import { store } from 'store';

import { globalConst, isBrowser, useScrollToTop } from 'modules/global';

import { IntlProvider, MainLayout } from 'components';

import 'styles/globals.scss';

const sfUiTextFont = localFont({
  preload: true,
  display: 'swap',
  src: [
    {
      weight: '300',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-light.otf'
    },
    {
      weight: '400',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-regular.otf'
    },
    {
      weight: '500',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-medium.otf'
    },
    {
      weight: '600',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-semibold.otf'
    },
    {
      weight: '700',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-bold.otf'
    },
    {
      weight: '800',
      style: 'normal',
      path: '../public/font/sf_ui/SFUIText-heavy.otf'
    }
  ]
});

export default function App({ Component, pageProps }: AppProps) {
  const { locale, defaultLocale, pathname, route } = useRouter();
  const currentLocale = locale || defaultLocale || 'uz';
  const mainRef = useRef<HTMLDivElement>(null);

  useScrollToTop(mainRef);

  const antdLocale = useMemo(
    () => globalConst.antdLocales[currentLocale as keyof typeof globalConst.antdLocales],
    [currentLocale]
  );
  const shouldHideHeader = !globalConst.hiddenHeaderRoutes.includes(route);
  const shouldHideTawk = globalConst.hiddenHeaderRoutes.some(hiddenRoute => {
    const pattern = hiddenRoute.replace(/\[.*?\]/g, '[^/]+').replace(/\//g, '\\/');
    const regex = new RegExp(`^${pattern}$`);

    return regex.test(route);
  });
  const isHome = pathname === '/';

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false
          }
        }
      })
  );

  const isPolicyPage = globalConst.policyPages.includes(pathname);

  useEffect(() => {
    if (isBrowser()) {
      if (!shouldHideTawk) {
        if (!window.Tawk_API) {
          const script = document.createElement('script');

          script.src = 'https://embed.tawk.to/68e2614f05f950195186500e/1j6q4o7lo';
          script.async = true;
          script.charset = 'UTF-8';
          script.setAttribute('crossorigin', '*');
          document.body.appendChild(script);
        } else {
          window.Tawk_API.showWidget();
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (window.Tawk_API) {
          window.Tawk_API.hideWidget();
        }
      }
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, shouldHideTawk]);

  return (
    <>
      <Head>
        <title>Akam</title>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {isPolicyPage && <meta name="robots" content="noindex, follow" />}
        <meta name="yandex-verification" content="4f3406ec52462b20" />
        <meta name="google-site-verification" content="M5PDUVGToEqdR9aVUIs_Z9mRS7XNTGmm-Yd_SSGNbqI" />
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
      </Head>
      <main
        ref={mainRef}
        id="root"
        className={classNames(
          sfUiTextFont.className,
          'w-full overflow-x-hidden lg:overflow-visible bg-primary transition-all duration-300',
          {
            'pt-0': !shouldHideHeader,
            'lg:pt-42.5 pt-33': shouldHideHeader && isHome,
            'lg:pt-25 pt-15': shouldHideHeader && !isHome
          }
        )}
      >
        <ConfigProvider
          locale={antdLocale}
          renderEmpty={() => <Empty />}
          message={{
            className: 'z-[1111111111111111]'
          }}
          theme={{
            token: {
              colorPrimary: globalConst.colors.primary
            }
          }}
        >
          <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
              <Provider store={store}>
                <QueryParamProvider adapter={NextAdapterApp}>
                  <IntlProvider messages={pageProps?.messages}>
                    <MainLayout>
                      <Component {...pageProps} />
                    </MainLayout>
                    <ReactQueryDevtools initialIsOpen={false} />
                  </IntlProvider>
                </QueryParamProvider>
              </Provider>
            </Hydrate>
          </QueryClientProvider>
        </ConfigProvider>
      </main>
    </>
  );
}
